import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  IconActivity, IconAlertTriangle, IconChartLine, IconChartPie, IconScale, IconSparkles, IconTargetArrow, IconWorld, IconBuildingBank, IconBuildingSkyscraper, IconChartBar, IconChevronDown, IconChevronLeft, IconCoin, IconFileText, IconId, IconInfoCircle, IconList, IconLink, IconLock, IconSearch, IconFilter,
  IconMail, IconMessageCircle, IconPackage, IconPencil, IconPrinter, IconReceipt, IconReceiptRefund, IconExternalLink, IconPhone, IconPlus, IconRoute, IconTrash, IconUpload, IconUserCircle, IconUsers, IconChecklist, type Icon,
} from '@tabler/icons-react';
import { DataError, useRecord } from '@hana/data';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { useT } from '@hana/i18n';
import { CustomerPadDetail } from './CustomerPadDetail';
import { Button, Chip, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { IconBell, IconDots, IconUser, IconUserPlus } from '@tabler/icons-react';
import { detailFor, IX_KINDS, JOB_SEGS, jobSegOf, MAIL_FLAGS, PERF_PERIODS, perfFor, type PerfPeriod, REFUND_LABEL, type ContactRow, type CustomerDetailData, type ZnsTpl, type IxKind, type JobRow, type JobSeg, type RefundState } from './detail';
import { KN_PERIODS, KN_SRC, knFor, type Chg, type KnPeriod } from './kn';
import { bucketOf, RF_DBN, RF_FILTER0, RF_PERIODS, RF_ST, RF_TPL, refundFor, refundPartner, rfDupOf, type RfBucket, type RfFilter, type RfRow } from './refund';
import { abbr, initials, myChip } from './view';
import s from './customer-detail.module.css';

// Mint-CustomerDetail (1536): đầu trang · 11 tab · tab Tổng quan chia 2:1 (trái 6 thẻ, phải 6 thẻ).
// Chỉ đọc; nút ghi báo toast. Người xem chưa có quyền `money`: công nợ chỉ trạng thái, số tiền ••••.
// Các tab khác (Liên hệ, Job, …) làm ở việc sau — bấm báo toast.

const TAB_KEYS = ['ov', 'ct', 'job', 'perf', 'kn', 'ix', 'cfg', 'ar', 'my', 'refund', 'wiki'] as const;
type TabKey = (typeof TAB_KEYS)[number];

function tabsOf(d: CustomerDetailData, t: (k: string) => string) {
  const c = d.tabCounts;
  return [
    { key: 'ov', label: t('crm.cdt.ov') },
    { key: 'ct', label: t('crm.cdt.ct'), n: c.contacts },
    { key: 'job', label: 'Job', n: c.jobs },
    { key: 'perf', label: t('crm.cdt.perf') },
    { key: 'kn', label: t('crm.cdt.kn') },
    { key: 'ix', label: t('crm.cdt.ix'), n: c.interactions },
    { key: 'cfg', label: t('crm.cdt.cfg') },
    { key: 'ar', label: t('crm.cdt.ar') },
    { key: 'my', label: 'myHANA', n: c.myHana },
    { key: 'refund', label: 'Refund' },
    { key: 'wiki', label: t('crm.cdt.wiki'), n: c.wiki },
  ] satisfies { key: TabKey; label: string; n?: number }[];
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

/** Thẻ có đầu: icon · tiêu đề · cụm nút · nút gập. `flush` = không đệm trong (bảng sát mép). */
export function Card({ title, label, icon: Ico, hint, actions, defaultOpen = true, flush, children }: { title: string; icon: Icon; hint?: string; label?: string; actions?: ReactNode; defaultOpen?: boolean; flush?: boolean; children?: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`${s.card} ${flush ? s.flush : ''}`} aria-label={label ?? title}>
      <div className={s.cardHd}>
        <span className={s.cardIc} aria-hidden="true">
          <Ico size={18} stroke={1.9} />
        </span>
        <h2 className={s.h2}>{title}</h2>
        {hint && (
          <span className={s.hint} role="img" aria-label={`Giải thích: ${hint}`} title={hint}>
            <IconInfoCircle size={15} stroke={1.8} aria-hidden="true" />
          </span>
        )}
        <span className={s.grow} />
        {actions}
        <button type="button" className={s.fold} aria-expanded={open} aria-label={`Mở hoặc gập ${title}`} onClick={() => setOpen(!open)}>
          <IconChevronDown size={16} stroke={2} className={open ? s.up : undefined} aria-hidden="true" />
        </button>
      </div>
      {open && children}
    </section>
  );
}

export const Av = ({ text }: { text: string }) => (
  <span className={s.av} aria-hidden="true">
    {abbr(text)}
  </span>
);

export function Kv({ rows, stack }: { rows: [string, ReactNode][]; stack?: boolean }) {
  return (
    <dl className={`${s.kv} ${stack ? s.stack : ''}`}>
      {rows.map(([k, v]) => (
        <div key={k} className={s.kvRow}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function ExportBars({ d }: { d: CustomerDetailData }) {
  const max = Math.max(1, ...d.exports.map((e) => e.value ?? 0));
  const label = d.exports.map((e) => `${e.month} ${e.value ?? 'chưa nạp'}`).join(', ');
  return (
    <div className={s.bars} role="img" aria-label={`Kim ngạch theo tháng (nghìn USD): ${label}`}>
      {d.exports.map((e, i) => (
        <div key={e.month + i} className={s.bar} title={e.value == null ? `${e.month}: chưa nạp` : `${e.month}: ${e.value}k USD`}>
          <span className={`${s.barFill} ${i >= 9 ? s.peak : ''} ${e.value == null ? s.none : ''}`} style={{ height: `${e.value == null ? 3 : Math.max(3, Math.round((e.value / max) * 52))}px` }} />
          <span className={s.barLb}>{e.month[0]}</span>
        </div>
      ))}
    </div>
  );
}

const AGING: [string, string][] = [
  ['Trong hạn', ''],
  ['Quá hạn 1–30 ngày', ''],
  ['Quá hạn 31–60 ngày', ''],
  ['Quá hạn trên 60 ngày', ''],
];

function MainContacts({ d }: { d: CustomerDetailData }) {
  const t = useT();
  const toast = useToast();
  return (
  <Card
                  title={t('crm.cda.k1')}
                  icon={IconPhone}
                  actions={
                    <button type="button" className={s.pill} onClick={() => toast({ msg: 'Tab này chưa mở ở bản này' })}>
                      Tất cả {d.contactTotal}
                    </button>
                  }
                >
                  {d.contacts.length === 0 ? (
                    <div className={s.empty}>{t('crm.cdt.chuaCoLienHe')}</div>
                  ) : (
                    d.contacts.map((c) => (
                      <div key={c.name} className={s.person}>
                        <span className={s.mark2} aria-hidden="true">
                          {initials(c.name)}
                        </span>
                        <div className={s.grow}>
                          <b>{c.name}</b>
                          <div className={s.lb}>{c.role}</div>
                        </div>
                        <button type="button" className={s.ic} aria-label={`Gọi ${c.name}`} title={`Gọi ${c.name}`} onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}>
                          <IconPhone size={15} stroke={1.9} aria-hidden="true" />
                        </button>
                        <button type="button" className={s.ic} aria-label={`Gửi mail ${c.name}`} title={`Gửi mail ${c.name}`} onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}>
                          <IconMail size={15} stroke={1.9} aria-hidden="true" />
                        </button>
                      </div>
                    ))
                  )}
                </Card>
  );
}

const shortName = (n: string) => n.split(' ').pop() ?? n;
const ZALO = { follow: 'crm.cdz.zalo.follow', unfollow: 'crm.cdz.zalo.unfollow', none: 'crm.cdz.zalo.none' } as const;

function ContactsTab({ d }: { d: CustomerDetailData }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const rows: ContactRow[] = d.contactRows;
  return (
    <div className={s.grid}>
      <div className={s.col}>
        <Card
          title={`Liên hệ · ${rows.length}`}
          label={t('crm.cda.k2')}
          icon={IconPhone}
          flush
          actions={
            <button type="button" className={s.pillPri} onClick={noWrite}>
              <IconPlus size={14} stroke={1.9} aria-hidden="true" />
              {t('crm.x.addContact')}
            </button>
          }
        >
          {rows.length === 0 ? (
            <div className={s.empty}>{t('crm.cdt.chuaCoLienHe')}</div>
          ) : (
            <>
              <div className={`${s.ctRow} ${s.jobHd}`} aria-hidden="true">
                <span>{t('crm.cdt.hoTenDienThoai')}</span>
                <span>Zalo</span>
                {MAIL_FLAGS.map((f) => (
                  <span key={f} className={s.ctCenter}>{f}</span>
                ))}
                <span />
              </div>
              {rows.map((c) => (
                <div key={c.name} className={s.ctRow}>
                  <div className={s.cell2}>
                    <span>
                      <b>{c.name}</b>
                      {c.owner && <b> {t('crm.cdt.chuSoHuu')}</b>} <span className={s.lb}>{c.role}</span>
                    </span>
                    <span className={s.tx2}>
                      {c.phone} · {c.mail}
                    </span>
                  </div>
                  <span className={s.st}>
                    <span className={s.stDot} style={{ background: c.zalo === 'follow' ? 'var(--ok)' : 'var(--lb)' }} aria-hidden="true" />
                    {ZALO[c.zalo]}
                  </span>
                  {MAIL_FLAGS.map((f) => (
                    <span key={f} className={s.ctCenter}>
                      <input type="checkbox" className={s.tick} checked={c.flags.includes(f)} onChange={noWrite} aria-label={`${c.name} nhận ${f}`} />
                    </span>
                  ))}
                  <span className={s.ctAct}>
                    <button type="button" className={s.linkAll} onClick={noWrite}>{t('crm.cdt.goi')}</button>
                    <button type="button" className={s.linkAll} onClick={noWrite}>
                      <IconPencil size={14} stroke={1.8} aria-hidden="true" />
                      {t('crm.x.edit')}
                    </button>
                    <button type="button" className={s.linkAll} onClick={noWrite}>
                      <IconTrash size={14} stroke={1.8} aria-hidden="true" />
                      {t('crm.x.delete')}
                    </button>
                  </span>
                </div>
              ))}
            </>
          )}
        </Card>
      </div>
      <aside className={s.col}>
        <Card title={t('crm.cda.k3')} icon={IconMail} hint={t('crm.cda.k4')}>
          {MAIL_FLAGS.map((f) => {
            const who = rows.filter((c) => c.flags.includes(f));
            return (
              <div key={f} className={s.flagRow}>
                <span className={s.flagK}>{f}</span>
                <span className={`${s.grow} ${s.lb} ${who.length ? '' : s.warnTx}`} title={who.map((c) => c.name).join(', ')}>
                  {who.length ? who.map((c) => shortName(c.name)).join(' · ') : 'Chưa ai nhận'}
                </span>
                <b className={who.length ? undefined : s.warnTx}>{who.length}</b>
              </div>
            );
          })}
        </Card>
        <MainContacts d={d} />
        <Card title={t('crm.cda.k5')} icon={IconUpload}>
          <button type="button" className={`${s.pillLine} ${s.wide}`} onClick={noWrite}>
            <IconUpload size={14} stroke={1.9} aria-hidden="true" />
            {t('crm.x.importXlsx')}
          </button>
        </Card>
      </aside>
    </div>
  );
}

const ZNS_TPL: Record<ZnsTpl, { k: string; cls: string; tip: string }> = {
  ok: { k: 'crm.cdz.ok', cls: 'tplOk', tip: 'crm.cdz.okTip' },
  wait: { k: 'crm.cdz.wait', cls: 'tplWait', tip: 'crm.cdz.waitTip' },
  rej: { k: 'crm.cdz.rej', cls: 'tplRej', tip: 'crm.cdz.rejTip' },
};
const ZALO_TIP = { follow: 'Follow', unfollow: 'Bỏ follow', none: 'Chưa liên kết' } as const;

function CfgTab({ d }: { d: CustomerDetailData }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const [on, setOn] = useState<Record<string, boolean>>(() => Object.fromEntries(d.znsRows.map((z) => [z.key, z.on])));
  const [who, setWho] = useState<Record<string, string[]>>(() => Object.fromEntries(d.znsRows.map((z) => [z.key, z.who])));
  const pick = (key: string, name: string) => {
    const cur = who[key] ?? [];
    setWho({ ...who, [key]: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] });
    noWrite();
  };
  return (
    <div className={s.grid}>
    <div className={s.col}>
      <Card
        title={t('crm.cda.k6')}
        icon={IconFileText}
        hint={t('crm.cda.k7')}
        actions={
          <button type="button" className={s.pillPri} onClick={noWrite}>
            <IconExternalLink size={14} stroke={1.8} aria-hidden="true" />
            {t('crm.x.openPrintCt')}
          </button>
        }
      >
        {d.contracts.length === 0 ? (
          <div className={s.empty}>{t('crm.cdt.chuaCoHopDong')}</div>
        ) : (
          d.contracts.map((c, i) => (
            <div key={c.name} className={s.cfgRow} style={i === 0 ? { borderTop: 0 } : undefined}>
              <span className={s.ixIc} aria-hidden="true">
                <IconFileText size={16} stroke={1.9} />
              </span>
              <div className={s.cell2}>
                <b>{c.name}</b>
                <span className={s.lb}>{c.desc}</span>
              </div>
              <span className={`${s.badge} ${c.ok ? s.tplOk : s.tplWait}`}>{c.status}</span>
            </div>
          ))
        )}
      </Card>
      <Card
        title={t('crm.cda.k8')}
        icon={IconMessageCircle}
        hint={t('crm.cda.k9')}
        actions={
          <button type="button" className={s.linkAll} onClick={noWrite}>
            <IconExternalLink size={14} stroke={1.8} aria-hidden="true" />
            {t('crm.x.znsTpl')}
          </button>
        }
      >
        <div className={`${s.znsRow} ${s.znsHd}`} aria-hidden="true">
          <span>{t('crm.cdt.mocJob')}</span>
          <span>{t('crm.cdt.bat')}</span>
          <span>{t('crm.cdt.baoCho')}</span>
          <span className={s.r}>{t('crm.cdt.mau')}</span>
        </div>
        {d.znsRows.map((z) => {
          const elig = d.contactRows.filter((c) => c.zalo !== 'none' && (!z.acc || /kế toán/i.test(c.role)));
          const tpl = ZNS_TPL[z.tpl];
          return (
            <div key={z.key} className={s.znsRow}>
              <span className={s.st}>
                <b>{z.name}</b>
                {z.acc && <IconLock size={13} stroke={2} aria-label={t('crm.cda.k10')} />}
              </span>
              <button type="button" role="switch" aria-checked={on[z.key]} aria-label={`Báo ZNS · ${z.name}`} className={`${s.sw} ${on[z.key] ? s.swOn : ''}`} onClick={() => { setOn({ ...on, [z.key]: !on[z.key] }); noWrite(); }}>
                <span />
              </button>
              <span className={`${s.chips} ${on[z.key] ? '' : s.dim}`}>
                {elig.map((c) => {
                  const p = (who[z.key] ?? []).includes(c.name);
                  return (
                    <button key={c.name} type="button" aria-pressed={p} className={`${s.whoChip} ${p ? s.whoOn : ''}`} title={`${c.name} · ${c.role} · ${t(ZALO_TIP[c.zalo])}`} onClick={() => pick(z.key, c.name)}>
                      <span className={s.stDot} style={{ background: c.zalo === 'follow' ? 'var(--ok)' : 'var(--lb)' }} aria-hidden="true" />
                      {c.name.split(' ').slice(-2).join(' ')}
                    </button>
                  );
                })}
                {elig.length === 0 && <span className={s.lb}>{t('crm.cdt.chuaCoLienHe2')}</span>}
              </span>
              <span className={`${s.badge} ${s[tpl.cls]} ${s.r}`} title={`${z.tid} · ${t(tpl.tip)}`}>
                <span className={s.stDot} style={{ background: 'currentColor' }} aria-hidden="true" />
                {t(tpl.k)}
              </span>
            </div>
          );
        })}
      </Card>
      <Card
        title={`Tài khoản ngân hàng của khách · ${d.banks.length}`}
        label={t('crm.cda.k11')}
        icon={IconBuildingBank}
        hint={t('crm.cda.k12')}
        actions={
          <button type="button" className={s.pillPri} onClick={noWrite}>
            <IconPlus size={14} stroke={1.9} aria-hidden="true" />
            {t('crm.x.addAccount')}
          </button>
        }
      >
        {d.banks.length === 0 ? (
          <div className={s.empty}>{t('crm.cdt.chuaCoTaiKhoan')}</div>
        ) : (
          d.banks.map((b) => (
            <div key={b.no} className={s.bankRow}>
              <div className={s.cell2}>
                <b>{b.bank}</b>
                <span className={s.lb}>Nhận hoàn tiền / cược{b.isDefault ? ' · mặc định' : ''}</span>
              </div>
              <b className={s.acct}>•••• {b.no.slice(-4)}</b>
              <span className={`${s.tx2} ${s.ell}`}>{b.holder}</span>
              <span className={`${s.badge} ${b.verified ? s.tplOk : s.tplWait}`}>{b.verified ? `Đã xác minh · ${b.verifiedBy}` : `Chờ xác minh · nhập bởi ${b.by}`}</span>
              <span className={s.bankAct}>
                {!b.isDefault && <button type="button" className={s.pillPri} onClick={noWrite}>{t('crm.cdt.datMacDinh')}</button>}
                {!b.verified && <button type="button" className={s.pillPri} onClick={noWrite}>{t('crm.cdt.xacMinh')}</button>}
                <button type="button" className={s.linkAll} onClick={noWrite}>{t('crm.cdt.ngungDung')}</button>
              </span>
            </div>
          ))
        )}
      </Card>
    </div>
    <aside className={s.col}>
      <Card title={t('crm.cda.k13')} icon={IconList} actions={<button type="button" className={s.pill} onClick={noWrite}><IconPencil size={14} stroke={1.8} aria-hidden="true" />{t('crm.cdt.suaCauHinh')}</button>}>
        {d.cfgRows.map(([k, v], i) => (
          <div key={k} className={s.kvRow} style={i === 0 ? { borderTop: 0 } : undefined}>
            <span className={s.lb}>{k}</span>
            <span className={s.kvV}>{v}</span>
          </div>
        ))}
        {d.freeTime.length > 0 && (
          <>
            <div className={`${s.lb} ${s.cfgCap}`}>{t('crm.cdt.freeTimeDemDet')}</div>
            <div className={s.ftBox}>
              <div className={`${s.ftRow} ${s.ftHd}`}><span>{t('crm.cdt.hangLoaiCont')}</span><span className={s.r}>DEM</span><span className={s.r}>DET</span><span className={s.r}>{t('crm.cdt.chuanHang')}</span></div>
              {d.freeTime.map((f) => (
                <div key={f.k} className={`${s.ftRow} ${s.ftBody}`}><span>{f.k}</span><b className={s.r}>{f.dem}</b><b className={s.r}>{f.det}</b><span className={`${s.r} ${s.lb}`}>{f.std}</span></div>
              ))}
            </div>
            <div className={`${s.lb} ${s.cfgCap}`}>{t('crm.cdt.maPhiChiHo')}</div>
            <div className={s.chips}>{d.feeCodes.map((f) => <span key={f} className={s.feeChip}>{f}</span>)}</div>
          </>
        )}
      </Card>
    </aside>
    </div>
  );
}

const JOB_DOT = { run: 'var(--d-amber)', ac: 'var(--d-blue)', rl: 'var(--d-green)', iv: 'var(--d-violet)', cx: 'var(--d-gray)' } as const;
const jobDot = (j: JobRow) =>
  j.invoiced ? JOB_DOT.iv : j.status === 'Cancelled' || j.status === 'No Show' ? JOB_DOT.cx : j.status === 'BL Released' ? JOB_DOT.rl : j.status.startsWith('RQ') || j.status === 'BK Standby' ? JOB_DOT.run : JOB_DOT.ac;
const picsOf = (sale: string) => sale.split(' · ').filter(Boolean).map((x) => ({ name: x.trim(), ini: x.replace(/\s*\d+%$/, '').trim().slice(0, 2).toUpperCase() }));
const REFUND_TONE: Record<RefundState, string | undefined> = { ready: s.rfReady, wait: s.rfWait, done: s.rfDone };

const IX_ICON: Record<string, Icon> = { Gọi: IconPhone, Gặp: IconUsers, Mail: IconMail, 'Mail MKT': IconMail, Zalo: IconMessageCircle, ZNS: IconFileText, Quà: IconPackage };

function IxTab({ d }: { d: CustomerDetailData }) {
  const t = useT();
  const toast = useToast();
  const [kind, setKind] = useState<IxKind>('Tất cả');
  const rows = d.ixRows.filter((r) => kind === 'Tất cả' || r.kind === kind);
  return (
    <div className={s.col}>
      <Card title={t('crm.cda.k14')} icon={IconMessageCircle} hint={t('crm.cda.k15')}>
        <div className={s.jobBar}>
          <Segmented label={t('crm.cda.k16')} value={kind} onChange={(k) => setKind(k as IxKind)} items={IX_KINDS.map((k) => ({ key: k, label: k }))} />
          <button type="button" className={s.pillPri} style={{ marginLeft: 'auto' }} onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}>
            <IconPlus size={14} stroke={1.9} aria-hidden="true" />
            {t('crm.x.logInteraction')}
          </button>
        </div>
        {rows.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaCoTuongTac')}</div>}
        {rows.map((x, i) => {
          const Ico = IX_ICON[x.kind] ?? IconMessageCircle;
          return (
            <div key={`${x.at}-${i}`} className={s.ix}>
              <span className={s.ixIc} aria-hidden="true">
                <Ico size={15} stroke={2} />
              </span>
              <div className={s.ixTx}>
                <div className={s.ixHd}>
                  <b>{x.kind} · {x.who}</b>
                  <span className={s.lb}>{x.at} · {x.by}</span>
                  {x.kind === 'Zalo' && (
                    <button type="button" className={s.linkSm} onClick={() => toast({ msg: 'Hei chưa mở ở bản này' })}>
                      Hei ↗
                    </button>
                  )}
                </div>
                <div className={s.tx2}>{x.note}</div>
                {x.fu && <div className={s.ixFu} style={{ color: x.fuTone === 'warn' ? 'var(--warn)' : 'var(--lb)' }}>{x.fu}</div>}
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

function ArTab({ d }: { d: CustomerDetailData }) {
  const t = useT();
  const tone = { warn: 'var(--warn)', err: 'var(--err)', lb: 'var(--lb)' } as const;
  return (
    <div className={s.col}>
      <Card title={`Hoá đơn chưa thu · ${d.invoices.length}`} label={t('crm.cda.k17')} icon={IconFileText} flush>
        {d.invoices.length === 0 && <div className={s.empty}>{t('crm.cdt.khongCoHoaDon')}</div>}
        {d.invoices.map((v) => (
          <div key={v.no} className={s.invRow}>
            <b style={{ fontWeight: 'var(--fw-label)' }}>{v.no}</b>
            <span className={s.tx2}>{v.job}</span>
            <span className={s.tx2}>Hạn {v.due}</span>
            <b className={s.invAmt} title={t('crm.cda.k18')}>••••</b>
            <span className={s.invLate} style={{ color: tone[v.tone] }}>{v.late}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function MyTab({ d }: { d: CustomerDetailData }) {
  const t = useT();
  const toast = useToast();
  return (
    <div className={s.col}>
      <Card
        title={`Người dùng myHANA · ${d.myUsers.length}`}
        label={t('crm.cda.k19')}
        icon={IconUser}
        hint={t('crm.cda.k20')}
        flush
        actions={
          <button type="button" className={s.pillPri} onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}>
            <IconUserPlus size={14} stroke={1.8} aria-hidden="true" />
            {t('crm.x.inviteUser')}
          </button>
        }
      >
        {d.myUsers.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaCoTaiKhoan2')}</div>}
        {d.myUsers.map((u) => (
          <div key={u.mail} className={s.myRow}>
            <div className={s.cell2}>
              <b style={{ fontWeight: 'var(--fw-label)' }}>{u.name}</b>
              <span className={s.lb}>{u.mail}</span>
            </div>
            <span className={s.tx2}>{u.scope}</span>
            <span className={`${s.badge} ${u.status === 'Đang dùng' ? s.tplOk : s.tplWait}`}>{u.status}</span>
            <span className={s.lb}>{u.last}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function WikiTab({ d }: { d: CustomerDetailData }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  return (
    <div className={s.col}>
      <Card
        title={t('crm.cda.k21')}
        icon={IconPencil}
        hint={t('crm.cda.k22')}
        actions={
          <button type="button" className={s.pillPri} onClick={noWrite}>
            <IconPlus size={16} stroke={1.8} aria-hidden="true" />
            {t('crm.x.note')}
          </button>
        }
      >
        <div className={s.wkBody}>
          {d.wikiNotes.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaCoGhiChu')}</div>}
          {d.wikiNotes.map((n, i) => (
            <div key={n.title} className={`${s.wkNote} ${i ? s.rfBt : ''}`}>
              <div className={s.cell2}>
                <b style={{ fontWeight: 'var(--fw-label)' }}>{n.title}</b>
                <span className={s.tx2} style={{ whiteSpace: 'pre-line' }}>{n.body}</span>
                <span className={s.lb}>{n.meta}</span>
              </div>
              <span className={s.wkAct}>
                <button type="button" className={s.wkBtn} aria-label={`Sửa ghi chú ${n.title}`} onClick={noWrite}>
                  <IconPencil size={16} stroke={1.8} aria-hidden="true" />
                  {t('crm.x.edit')}
                </button>
                <button type="button" className={s.wkBtn} aria-label={`Xoá ghi chú ${n.title}`} onClick={noWrite}>
                  <IconTrash size={16} stroke={1.8} aria-hidden="true" />
                  {t('crm.x.delete')}
                </button>
              </span>
            </div>
          ))}
        </div>
      </Card>
      <Card
        title={t('crm.cda.k23')}
        icon={IconFileText}
        hint={t('crm.cda.k24')}
        actions={
          <button type="button" className={s.pillPri} onClick={noWrite}>
            <IconPlus size={16} stroke={1.8} aria-hidden="true" />
            {t('crm.x.upload')}
          </button>
        }
      >
        <div className={s.wkBody}>
          {d.wikiFiles.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaCoTepNao')}</div>}
          {d.wikiFiles.map((f, i) => (
            <div key={f.name} className={`${s.wkFile} ${i ? s.rfBt : ''}`}>
              <span className={s.wkName}>
                <span className={s.wkIc} aria-hidden="true">
                  <IconFileText size={15} stroke={1.9} />
                </span>
                <b>{f.name}</b>
              </span>
              <span className={s.tx2}>{f.kind}</span>
              <span className={s.lb} style={{ fontSize: 'inherit' }}>{f.size}</span>
              <span className={s.lb}>{f.meta}</span>
              <span className={s.wkAct}>
                <button type="button" className={s.pillPri} aria-label={`Xem ${f.name}`} onClick={() => toast({ msg: 'Trình xem tệp chưa mở ở bản này' })}>
                  <IconSearch size={16} stroke={1.8} aria-hidden="true" />
                  Xem
                </button>
                <button type="button" className={s.wkBtn} aria-label={`Xoá ${f.name}`} onClick={noWrite}>
                  <IconTrash size={16} stroke={1.8} aria-hidden="true" />
                  {t('crm.x.delete')}
                </button>
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function JobsTab({ d }: { d: CustomerDetailData }) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const [seg, setSeg] = useState<JobSeg>('all');
  const rows = d.jobRows.filter((j) => jobSegOf(j, seg));
  return (
    <div className={s.col}>
      <Card title={t('crm.cda.k25')} icon={IconPackage} hint={t('crm.cda.k26')}>
        <div className={s.jobBar}>
          <Segmented
            label={t('crm.cda.k27')}
            value={seg}
            onChange={(k) => setSeg(k as JobSeg)}
            items={JOB_SEGS.map((g) => ({ key: g.key, label: g.label, n: d.jobRows.filter((j) => jobSegOf(j, g.key)).length }))}
          />
          <span className={s.lb} style={{ marginLeft: 'auto' }}>{t('crm.cdt.jobTruocNgayChuyen')}</span>
        </div>
        {rows.length === 0 && <div className={s.empty}>{t('crm.cdt.khongCoJobO')}</div>}
        <div className={`${s.jobRow} ${s.jobHd} ${s.bleed}`} aria-hidden="true">
          <span>Job</span>
          <span>{t('crm.cdt.tuyen')}</span>
          <span>ETD</span>
          <span>{t('crm.cdt.trangThai')}</span>
          <span>PIC</span>
        </div>
        {rows.map((j) => (
          <div key={j.code} className={`${s.jobRow} ${s.bleed}`}>
            <span className={s.jobCode}>
              <button type="button" className={s.link} onClick={() => navigate({ to: '/ezy/jobs/$jobId', params: { jobId: j.code } })}>
                {j.code}
              </button>
              <span className={`${s.tag} ${j.kind === 'TR' ? s.tagTr : ''}`}>{j.kind}</span>
            </span>
            <span className={s.cell2}>
              <span title={j.route}>{j.route}</span>
              <span className={s.lb}>
                {j.box} · {j.cnee} · {j.vessel}
              </span>
            </span>
            <span className={s.tx2}>{j.etd}</span>
            <span className={s.st} title={j.invoiced ? `${j.status} · Đã xuất HĐ` : j.status}>
              <span className={s.stDot} style={{ background: jobDot(j), width: 8, height: 8 }} aria-hidden="true" />
              {j.status}
              {j.invoiced && (
                <span role="img" aria-label={t('crm.cda.k28')} title={t('crm.cda.k28')} className={s.inv}>
                  <IconReceipt size={14} stroke={1.8} aria-hidden="true" />
                </span>
              )}
            </span>
            <span className={s.pics} title={j.sale} aria-label={`PIC ${j.sale}`}>
              {picsOf(j.sale).map((a) => (
                <span key={a.name} className={s.avLg} title={a.name}>
                  {a.ini}
                </span>
              ))}
            </span>
          </div>
        ))}
      </Card>
      <Card
        title={`Hoàn phí (refund) · ${d.refunds.length}`}
        label={t('crm.cda.k29')}
        icon={IconReceiptRefund}
        hint={t('crm.cda.k30')}
        actions={
          <button type="button" className={s.linkAll} onClick={() => toast({ msg: 'Màn Phải trả chưa mở ở bản này' })}>
            <IconExternalLink size={14} stroke={1.8} aria-hidden="true" />
            {t('crm.x.openPayable')}
          </button>
        }
      >
        {d.refunds.map((r, i) => (
          <div key={r.job} className={`${s.rfRow} ${i ? s.rfBt : ''}`}>
            <button type="button" className={s.link} style={{ justifySelf: 'start' }} onClick={() => navigate({ to: '/ezy/jobs/$jobId', params: { jobId: r.job } })}>
              {r.job}
            </button>
            <span>{r.what}</span>
            <span className={`${s.rfChip} ${REFUND_TONE[r.state]}`}>{REFUND_LABEL[r.state]}</span>
            <span className={s.ctAct}>
              {r.state === 'ready' && (
                <button type="button" className={s.pillPri} onClick={noWrite}>
                  <IconReceipt size={14} stroke={1.8} aria-hidden="true" />
                  {t('crm.x.payRequest')}
                </button>
              )}
              <button type="button" className={s.linkAll} onClick={noWrite}>
                <IconPrinter size={14} stroke={1.8} aria-hidden="true" />
                {t('crm.x.printSlip')}
              </button>
            </span>
          </div>
        ))}
      </Card>
    </div>
  );
}

const RF_FILTERS: { key: 'all' | RfBucket; label: string; dot?: string }[] = [
  { key: 'all', label: 'crm.cdr.f.all' },
  { key: 'pend', label: 'crm.cdr.f.pend', dot: RF_ST.pend.dot },
  { key: 'wait', label: 'crm.cdr.f.wait', dot: RF_ST.wait.dot },
  { key: 'chi', label: 'crm.cdr.f.chi', dot: RF_ST.rq.dot },
  { key: 'paid', label: 'crm.cdr.f.paid', dot: RF_ST.paid.dot },
  { key: 'nil', label: 'crm.cdr.f.nil', dot: RF_ST.nil.dot },
];
const rfHeads = (code: 'KB' | 'COM'): [string, string?][] => [['Job'], ['Ngày job'], ['Người nhận', code === 'KB' ? 'Liên hệ hãng có cờ Collaborator · lọc theo Sale tuyến' : 'Liên hệ khách có cờ Collaborator'], [code === 'KB' ? 'Tuyến' : 'Sale'], ['Vol'], ['USD/cont'], ['tr ₫'], ['DN', 'Khách đã trả DN của job chưa (INVOUT)'], ['Trạng thái']];

export function RefundTab({ partner, code = 'COM', empty }: { partner: string; code?: 'KB' | 'COM'; empty: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const [f, setF] = useState<RfFilter>(RF_FILTER0);
  const [sub, setSub] = useState<'raw' | 'rfd'>('raw');
  const [flt, setFlt] = useState(false);
  const v = refundFor(partner, f, empty, code);
  const isDup = rfDupOf(v.inPeriod);
  const patch = (x: Partial<RfFilter>) => setF({ ...f, ...x });
  const rowSt = (x: RfRow) => RF_ST[x.st];
  const filtN = (f.roe ? 1 : 0) + (f.dbn ? 1 : 0) + (f.dup ? 1 : 0);
  const chips = [f.q ? { k: `“${f.q}”`, x: () => patch({ q: '' }) } : null, f.roe ? { k: t('crm.cdr.c.roe'), x: () => patch({ roe: false }) } : null, f.dup ? { k: t('crm.cdr.c.dup'), x: () => patch({ dup: false }) } : null, f.dbn ? { k: t('crm.cdr.c.dbn'), x: () => patch({ dbn: false }) } : null].filter((c) => c !== null);
  const anyF = f.status !== 'all' || f.q !== '' || filtN > 0;
  const seg = RF_FILTERS.filter((x) => x.key !== 'all' && x.key !== 'nil');
  const byColl = new Map<string, RfRow[]>();
  v.periodAll.filter((x) => x.collab !== 'NIL').forEach((x) => byColl.set(x.collab, [...(byColl.get(x.collab) ?? []), x]));
  const recipients = [...byColl.entries()].sort(([a], [b]) => (a < b ? -1 : 1));
    const alerts = [
    v.missing ? { k: `Thiếu khai · ${v.missing} job`, dot: 'var(--d-amber)', tip: `Job trong kỳ chưa có dòng ${code}`, on: false, act: 'Xem', run: () => toast({ msg: 'Danh sách job chưa khai chưa mở ở bản này' }) } : null,
    v.roeGap ? { k: `Thiếu tỷ giá · ${v.roeGap} dòng`, dot: 'var(--err)', tip: 'Amount = 0 vì tỷ giá ngày job chưa đồng bộ · chặn gom RFD', on: f.roe, act: f.roe ? 'Bỏ lọc' : 'Lọc', run: () => { setSub('raw'); patch({ roe: !f.roe }); } } : null,
    code === 'KB' && v.dupGap ? { k: `Trùng KB · ${v.dupGap} dòng`, dot: 'var(--err)', tip: 'Cùng job, cùng người nhận có ≥ 2 dòng KB', on: f.dup, act: f.dup ? 'Bỏ lọc' : 'Lọc', run: () => { setSub('raw'); patch({ dup: !f.dup }); } } : null,
    code === 'COM' && v.dbnGap ? { k: `Khách chưa trả DN · ${v.dbnGap}`, dot: 'var(--d-amber)', tip: 'COM chỉ nên chi khi khách đã trả DN của job', on: f.dbn, act: f.dbn ? 'Bỏ lọc' : 'Lọc', run: () => { setSub('raw'); patch({ dbn: !f.dbn }); } } : null,
  ].filter((a) => a !== null);
  return (
    <div className={s.grid}>
      <div className={s.col}>
        <Card title={`Refund ${code}`} icon={IconReceiptRefund} actions={<Segmented label={t('crm.cda.k31')} value={f.period} onChange={(k) => patch({ period: k })} items={RF_PERIODS.map(([k]) => ({ key: k, label: `${k}/2026` }))} />} hint={code === 'KB' ? 'OpsCostData TypeCost Refund, Code KB · Partner = hãng tàu của job (ShortName hồ sơ này) · người nhận lọc theo Sale tuyến' : 'OpsCostData TypeCost Refund, Code COM · Partner = khách của job (ShortName hồ sơ này) · người nhận là liên hệ Collaborator của khách'}>
          <div className={s.rfBar}>
            <Segmented label={t('crm.cda.k32')} value={sub} onChange={(k) => setSub(k as 'raw' | 'rfd')} items={[{ key: 'raw', label: `Dòng ${code} ${v.inPeriod.length}` }, { key: 'rfd', label: `Phiếu RFD ${v.docs.length}` }]} />
          </div>
          <div className={s.rfNote} role="note" title={t('crm.cda.k33')}>
            <IconLock size={15} stroke={2} aria-hidden="true" />
            {t('crm.x.moneyHidden')}<span className={s.lb}>{t('crm.cdt.doBangSoDong')}</span>
          </div>
          {sub === 'raw' && (
            <div className={s.rfBar}>
              <div className={s.rfFst} role="radiogroup" aria-label={t('crm.cda.k34')}>
                {RF_FILTERS.map((x) => (
                  <button key={x.key} type="button" role="radio" aria-checked={f.status === x.key} className={`${s.rfFb} ${f.status === x.key ? s.rfFbOn : ''}`} title={t(x.label)} onClick={() => patch({ status: x.key })}>
                    {x.dot && <span className={s.knDot} style={{ background: x.dot }} />}
                    {t(x.label)}
                    <span className={s.lb}>{v.counts[x.key]}</span>
                  </button>
                ))}
              </div>
              <label className={s.rfSearch}>
                <IconSearch size={15} stroke={2} aria-hidden="true" />
                <input aria-label={t('crm.cda.k35')} placeholder={t('crm.cda.k36')} value={f.q} onChange={(e) => patch({ q: e.target.value })} />
              </label>
              <span className={s.rfPop}>
                <button type="button" className={s.pill} aria-haspopup="dialog" aria-expanded={flt} onClick={() => setFlt(!flt)}>
                  <IconFilter size={14} stroke={1.8} aria-hidden="true" />
                  {t('crm.x.filter')}
                </button>
                {flt && (
                  <div role="dialog" aria-label={t('crm.cda.k37')} className={s.rfMenu}>
                    {([['roe', 'Thiếu tỷ giá', v.inPeriod.filter((x) => !x.roe).length], ...(code === 'KB' ? [['dup', 'Trùng KB', v.dupGap]] as const : []), ['dbn', 'Khách chưa trả DN', v.inPeriod.filter((x) => x.dbn !== 'Done').length]] as const).map(([key, label, n]) => (
                      <label key={key}>
                        <input type="checkbox" checked={f[key]} onChange={() => patch({ [key]: !f[key] })} />
                        <span>{label}</span>
                        <span className={s.lb}>{n}</span>
                      </label>
                    ))}
                    <div className={s.lb}>{v.rows.length} dòng khớp</div>
                  </div>
                )}
              </span>
              {chips.slice(0, 2).map((c) => (
                <button key={c.k} type="button" className={s.rfChipX} aria-label={t('crm.cdr.unfilter', { v: c.k })} onClick={c.x}>
                  {c.k} <span aria-hidden="true">✕</span>
                </button>
              ))}
              <span className={s.lb}>{anyF ? `${v.rows.length} / ${v.inPeriod.length} dòng` : `${v.inPeriod.length} dòng`}</span>
            </div>
          )}
        </Card>
        {sub === 'raw' && (
          <section className={`${s.card} ${s.flush}`} aria-label={`Dòng ${code}`}>
            <div className={`${s.rfRowG} ${s.rfHd}`}>
              {rfHeads(code).map(([k, tip]) => (
                <span key={k} title={tip ?? k} className={k === 'USD/cont' || k === 'tr ₫' ? s.r : k === 'DN' ? s.ctr : undefined}>{k}</span>
              ))}
            </div>
            {v.rows.length === 0 && <div className={s.empty}>{empty ? `Kỳ này chưa có dòng ${code}` : anyF ? 'Không có dòng khớp lọc' : `Kỳ này ${v.partner} chưa có dòng ${code}`}</div>}
            {v.rows.map((x) => (
              <div key={x.id} className={`${s.rfRowG} ${s.rfBd}`}>
                <button type="button" className={s.link} style={{ justifySelf: 'start' }} onClick={() => navigate({ to: '/ezy/jobs/$jobId', params: { jobId: x.job } })}>
                  {x.job}
                  {isDup(x) && <span className={s.rfDup} title={t('crm.cda.k38')} />}
                </button>
                <span className={s.tx2}>{x.date}</span>
                <span className={s.ell} title={x.collab === 'NIL' ? 'Không có người nhận' : `${x.partner}_•••• · tên và TK nhận ẩn`}>{x.collab === 'NIL' ? '—' : `${x.collab} ••••`}</span>
                <span className={`${s.tx2} ${s.ell}`}>{code === 'KB' ? `${x.pol} → ${x.pod}` : x.sale}</span>
                <span>{x.vol}×{x.ct}</span>
                <span className={`${s.r} ${s.lb}`}>••••</span>
                <span className={`${s.r} ${s.lb}`} title={t('crm.cda.k39')}>••••</span>
                <span className={s.ctr} title={RF_DBN[x.dbn].label}>
                  <span className={s.rfDbn} style={{ background: RF_DBN[x.dbn].dot, boxShadow: x.dbn === 'Pending' ? 'inset 0 0 0 1.5px var(--lb)' : undefined }} />
                </span>
                <span className={s.rfSt} title={rowSt(x).label + (x.doc ? ` · ${x.doc}` : '') + (!x.roe && x.st === 'pend' ? ' · thiếu tỷ giá' : '')}>
                  <span className={s.knDot} style={{ background: rowSt(x).dot }} />
                  {rowSt(x).label}
                </span>
              </div>
            ))}
            <div className={s.rfFoot}>
              <span className={s.lb}>{t('crm.cdt.soTienAn')}</span>
              <span className={s.rfLeg}>
                {Object.values(RF_DBN).map((x) => (
                  <span key={x.label} className={s.knLegI}>
                    <span className={s.knDot} style={{ background: x.dot }} />
                    {x.label.replace('Khách ', '')}
                  </span>
                ))}
              </span>
            </div>
          </section>
        )}
        {sub === 'rfd' && (
          <section className={`${s.card} ${s.flush}`} aria-label={t('crm.cda.k40')}>
            <div className={`${s.rfdG} ${s.rfHd}`}>
              {['Số RFD', 'Ngày', 'Người nhận', 'Dòng/job', 'Mẫu', 'Thực chi tr ₫', 'Trạng thái'].map((k) => (
                <span key={k} className={k === 'Dòng/job' || k === 'Thực chi tr ₫' ? s.r : undefined}>{k}</span>
              ))}
            </div>
            {v.docs.length === 0 && <div className={s.empty}>{v.partner} chưa có phiếu RFD trong kỳ</div>}
            {v.docs.map((p) => {
              const rows = v.inPeriod.filter((x) => x.doc === p.doc);
              return (
                <button key={p.doc} type="button" className={`${s.rfdG} ${s.rfBd} ${s.rfdBtn}`} onClick={() => toast({ msg: 'Chi tiết phiếu RFD chưa mở ở bản này' })}>
                  <b style={{ fontWeight: 'var(--fw-label)' }}>{p.doc}</b>
                  <span className={s.tx2}>{p.date}</span>
                  <span>{p.collab} ••••</span>
                  <span className={s.r}>{rows.length} / {new Set(rows.map((x) => x.job)).size}</span>
                  <span>{RF_TPL[p.tpl]}</span>
                  <span className={`${s.r} ${s.lb}`}>••••</span>
                  <span className={s.rfSt}>
                    <span className={s.knDot} style={{ background: RF_ST[p.st].dot }} />
                    {RF_ST[p.st].label}
                  </span>
                </button>
              );
            })}
            <div className={s.rfFoot}>
              <span className={s.lb}>{t('crm.cdt.duyetAccCaKe')}</span>
            </div>
          </section>
        )}
      </div>
      <div className={s.col}>
        <Card title={t('crm.cda.k41')} icon={IconChartBar} hint={t('crm.cda.k42')}>
          <div className={s.rfKpi}>
            {[
              ['Tổng refund', '••••', 'cần quyền tiền', `Tổng VND = Vol × USD/cont × tỷ giá ngày job, các dòng ${code} trong kỳ`],
              ['Dòng / job', `${v.inPeriod.length} / ${v.jobs}`, `kỳ ${f.period}/2026`, 'Số dòng refund / số job có dòng'],
              ['Job có refund', v.coverage ? `${Math.round(v.coverage * 100)}%` : '—', `${Math.round(v.coverage * (v.jobs + v.missing))} / ${v.jobs + v.missing} job trong kỳ`, `Job trong kỳ (bỏ Cancel / No Show) đã có dòng ${code}`],
              ['Job chưa có refund', String(v.missing), v.missing ? 'Xem · chép JobID' : 'Đủ', `Job active trong kỳ chưa có dòng ${code}`],
            ].map(([k, val, sub2, tip]) => (
              <div key={k} title={tip} className={s.rfKpiI}>
                <div className={s.lb}>{k}</div>
                <b>{val}</b>
                <div className={s.lb} style={k === 'Job chưa có refund' && v.missing ? { color: 'var(--warn)' } : undefined}>{sub2}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card title={t('crm.cda.k43')} icon={IconChecklist} hint={t('crm.cda.k44')}>
          <div className={s.lb}>{t('crm.cdt.gomRfdDoAcc')}</div>
          {[`Xuất Excel kỳ ${f.period}/2026 · ${v.partner}`, 'Mẫu thanh toán & nhịp chi'].map((k) => (
            <button key={k} type="button" className={s.rfAct} onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}>
              <span>{k}</span>
              <span aria-hidden="true">›</span>
            </button>
          ))}
        </Card>
        <Card title={t('crm.cda.k45')} icon={IconAlertTriangle} hint={t('crm.cda.k46')} actions={alerts.length > 0 ? <span className={s.rfCount}>{alerts.length}</span> : undefined}>
          {alerts.length === 0 && <div className={s.empty}>{t('crm.cdt.khongCoCanhBao')}</div>}
          {alerts.map((a) => (
            <button key={a.k} type="button" className={`${s.rfAlert} ${a.on ? s.rfAlertOn : ''}`} title={a.tip} onClick={a.run}>
              <span className={s.knDot} style={{ background: a.dot }} />
              <span className={s.grow}>{a.k}</span>
              <span className={s.lb}>{a.act}</span>
            </button>
          ))}
        </Card>
        <Card title={t('crm.cda.k47')} icon={IconUsers} hint={t('crm.cda.k48')}>
          <div className={s.tx2}>{t('crm.cdt.accCaKeToan')}</div>
          <div style={{ color: 'var(--warn)', fontWeight: 'var(--fw-label)' }}>{t('crm.cdt.gheTrongLeoLen')}</div>
        </Card>
        <Card title={t('crm.cda.k49')} icon={IconUserCircle} hint={t('crm.cda.k50')}>
          {recipients.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaCoNguoiNhan')}</div>}
          {recipients.map(([c, rr], i) => (
            <button key={c} type="button" className={`${s.rfRcv} ${i ? s.rfBd : ''} ${f.coll === c ? s.rfAlertOn : ''}`} title={`${v.partner}_•••• · tên và TK nhận ẩn · bấm để lọc`} onClick={() => patch({ coll: f.coll === c ? '' : c })}>
              <Av text={c.replace(/^CTV /, '')} />
              <span className={s.cell2}>
                <b style={{ fontWeight: 'var(--fw-label)' }}>{c} ••••</b>
                <span className={s.lb}>{rr.length} dòng · {rr.filter((x) => x.st === 'pend').length} chờ gom</span>
              </span>
              <span className={s.lb}>{rr.filter((x) => ['pend', 'wait', 'chi'].includes(bucketOf(x.st))).length}</span>
            </button>
          ))}
          <div className={s.lb}>{t('crm.cdt.dongChuaChiTao')}</div>
        </Card>
        <Card title={t('crm.cda.k51')} icon={IconChartPie} hint={t('crm.cda.k52')}>
          <div className={s.rfStack} role="img" aria-label={seg.map((x) => `${t(x.label)} ${v.counts[x.key]}`).join(', ')}>
            {seg.filter((x) => v.counts[x.key] > 0).map((x) => (
              <div key={x.key} title={`${t(x.label)} · ${v.counts[x.key]}`} style={{ flex: `${v.counts[x.key]} 1 0`, background: x.dot }} />
            ))}
          </div>
          <div className={s.rfLegG}>
            {seg.map((x) => (
              <button key={x.key} type="button" className={s.rfLegB} onClick={() => { setSub('raw'); patch({ status: x.key }); }}>
                <span className={s.knDot} style={{ background: x.dot }} />
                <span className={s.grow}>{t(x.label)}</span>
                <b>{v.counts[x.key]}</b>
              </button>
            ))}
          </div>
        </Card>
        <Card title={t('crm.cda.k53')} icon={IconChartBar} hint={t('crm.cda.k54')}>
          <div className={s.lb}>{code} · số job</div>
          <div className={s.rfCols} role="img" aria-label={`6 kỳ: ${v.six.map((c) => `${c.k} ${c.n}`).join(', ')}`}>
            {v.six.map((c, i) => {
              const max = Math.max(1, ...v.six.map((z) => z.n));
              const cur = i === 3 + RF_PERIODS.findIndex(([k]) => k === f.period);
              return (
                <div key={c.k} className={s.rfCol} title={`${c.k} · ${c.n}`}>
                  <span className={cur ? s.rfNCur : s.lb}>{c.n}</span>
                  <span className={s.rfBarV} style={{ height: `${Math.max(3, Math.round((c.n / max) * 100))}%`, opacity: cur ? 1 : i < 3 ? 0.4 : 0.7 }} />
                  <span className={s.lb}>{c.k}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

function LineChart({ perf }: { perf: ReturnType<typeof perfFor> }) {
  const W = 480, H = 190, x0 = 24, top = 22, yb = H - 26;
  const cur = perf.months.map((m) => m.n);
  const mx = Math.max(1, ...cur, ...(perf.prev ?? [])) * 1.2;
  const pts = (a: number[]) => a.map((v, i) => [x0 + (i * (W - 2 * x0)) / Math.max(1, a.length - 1), yb - (v / mx) * (yb - top)] as const);
  const path = (p: (readonly [number, number])[]) => p.map((q, i) => `${i ? 'L' : 'M'}${q[0].toFixed(1)} ${q[1].toFixed(1)}`).join(' ');
  const P = pts(cur);
  const d = path(P);
  return (
    <svg className={s.chart} role="img" aria-label={perf.aria} width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <line x1="10" y1={yb} x2="470" y2={yb} stroke="var(--line)" />
      <path d={`${d} L${P[P.length - 1]![0].toFixed(1)} ${yb} L${P[0]![0].toFixed(1)} ${yb} Z`} fill="var(--d-blue)" opacity="0.08" />
      {perf.prev && <path d={path(pts(perf.prev))} fill="none" stroke="var(--d-amber)" strokeWidth="2" strokeDasharray="5 5" />}
      <path d={d} fill="none" stroke="var(--d-blue)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      {P.map((q, i) => (
        <g key={perf.months[i]!.m}>
          <text className={s.chVal} x={q[0]} y={q[1] - 9} textAnchor="middle">{cur[i]}</text>
          <text className={s.chTx} x={q[0]} y="184" textAnchor="middle">{perf.months[i]!.m.split('/')[0]}</text>
          <circle cx={q[0]} cy={q[1]} r={i === P.length - 1 ? 4.5 : 3} fill="var(--card)" stroke="var(--d-blue)" strokeWidth="2.5">
            <title>{`${perf.months[i]!.m}: ${cur[i]} job · ${perf.months[i]!.teu} TEU`}</title>
          </circle>
        </g>
      ))}
    </svg>
  );
}

function PerfTab({ empty }: { empty: boolean }) {
  const t = useT();
  const [period, setPeriod] = useState<PerfPeriod>('6M');
  const perf = perfFor(period, empty);
  return (
    <div className={s.col}>
      <div className={s.pfBar}>
        <Segmented label={t('crm.cda.k55')} value={period} onChange={(k) => setPeriod(k as PerfPeriod)} items={PERF_PERIODS.map((k) => ({ key: k, label: k }))} />
        <span className={s.lb}>{perf.range}</span>
      </div>
      <Card title={t('crm.cda.k56')} icon={IconChartBar} hint={t('crm.cda.k57')}>
        <div style={{ marginTop: 8 }}>
          <LineChart perf={perf} />
        </div>
      </Card>
      <Card title={t('crm.cda.k58')} icon={IconRoute} hint={t('crm.cda.k59')}>
        <div className={s.routes} role="img" aria-label={perf.laneAria}>
          <div className={`${s.rtRow} ${s.rtHd}`}>
            <span>{t('crm.cdt.tuyenHangChinh')}</span>
            <span>{t('crm.cdt.tyTrongJob')}</span>
            <span className={s.r}>Job</span>
            <span className={s.r}>TEU</span>
          </div>
          {perf.lanes.map((l) => (
            <div key={l.k} className={`${s.rtRow} ${s.rtBody}`}>
              <span className={s.cell2}>
                <span>{l.k}</span>
                <span className={s.lb}>{l.carriers}</span>
              </span>
              <div className={s.track}>
                <span className={s.fill} style={{ width: `${l.w}%` }} />
              </div>
              <b className={s.r} style={{ fontWeight: 'var(--fw-label)' }}>{l.jobs}</b>
              <span className={`${s.r} ${s.tx2}`}>{l.teu}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const ChgTag = ({ c, tip }: { c: Chg; tip?: string }) => (
  <span className={`${s.chg} ${c.up ? s.chgUp : s.chgDn}`} title={tip}>{c.k}</span>
);

function KnLine({ kn }: { kn: ReturnType<typeof knFor> }) {
  const n = kn.pts.length;
  const mx = Math.max(...kn.pts.map((p) => Math.max(p.v ?? 0, p.prev))) * 1.12;
  const X = (j: number) => Math.round(n > 1 ? 40 + (j * 640) / (n - 1) : 360);
  const Y = (v: number) => Math.round(176 - (v / mx) * 140);
  const line = (get: (p: (typeof kn.pts)[number]) => number | null) => {
    let pen = false;
    return kn.pts.map((p, j) => { const v = get(p); if (v === null) { pen = false; return ''; } const c = `${pen ? 'L' : 'M'}${X(j)} ${Y(v)}`; pen = true; return c; }).filter(Boolean).join(' ');
  };
  const segs: number[][] = [];
  let cur: number[] = [];
  kn.pts.forEach((p, j) => { if (p.v === null) { if (cur.length) segs.push(cur); cur = []; } else cur.push(j); });
  if (cur.length) segs.push(cur);
  const area = segs.filter((g) => g.length > 1).map((g) => `${g.map((j, k) => `${k ? 'L' : 'M'}${X(j)} ${Y(kn.pts[j]!.v!)}`).join(' ')} L${X(g[g.length - 1]!)} 176 L${X(g[0]!)} 176 Z`).join(' ');
  return (
    <svg className={s.chart} role="img" aria-label={kn.lineAria} width="100%" height={206} viewBox="0 0 720 206">
      <line x1="20" y1="176" x2="700" y2="176" stroke="var(--line)" />
      {kn.peak && <rect x={X(kn.peak[0]) - 28} y="20" width={X(kn.peak[1]) - X(kn.peak[0]) + 56} height="156" rx="10" fill="var(--d-amber)" opacity="0.1" />}
      {area && <path d={area} fill="var(--d-blue)" opacity="0.08" />}
      <path d={line((p) => p.prev)} fill="none" stroke="var(--d-amber)" strokeWidth="2" strokeDasharray="5 5" />
      <path d={line((p) => p.v)} fill="none" stroke="var(--d-blue)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      {kn.pts.map((p, j) => (
        <g key={p.i}>
          <text className={s.chVal} x={X(j)} y={p.v === null ? 168 : Y(p.v) - 12} textAnchor="middle">{p.v === null ? 'chưa nạp' : p.v}</text>
          <text className={s.chTx} x={X(j)} y="198" textAnchor="middle">{p.m}</text>
          {p.v !== null && (
            <circle cx={X(j)} cy={Y(p.v)} r={4} fill="var(--card)" stroke="var(--d-blue)" strokeWidth="2.5"><title>{p.tip}</title></circle>
          )}
        </g>
      ))}
    </svg>
  );
}

const Legend = ({ items, note }: { items: [string, string][]; note: string }) => (
  <div className={s.knLeg}>
    {items.map(([k, c]) => (
      <span key={k} className={s.knLegI}><span className={s.knDot} style={{ background: c }} />{k}</span>
    ))}
    <span className={s.grow} />
    <span>{note}</span>
  </div>
);

function KnTab({ empty }: { empty: boolean }) {
  const t = useT();
  const toast = useToast();
  const [period, setPeriod] = useState<KnPeriod>('1Y');
  const kn = knFor(period, empty);
  const soon = (msg: string) => () => toast({ msg });
  if (empty) {
    return (
      <div className={s.col}>
        <Card title={t('crm.cda.k60')} icon={IconChartLine}>
          <div className={s.empty}>{t('crm.cdt.chuaCoToKhai')}</div>
        </Card>
      </div>
    );
  }
  return (
    <div className={s.grid}>
      <div className={s.col}>
      <Card title={t('crm.cda.k61')} icon={IconChartLine} hint={`Kim ngạch (USDAmount), số tờ khai (CustomsNo) và số lô (Shipments) theo tháng (Year, Month). Chọn kỳ 3M / 6M / 1Y / YTD; so cùng kỳ năm trước chỉ trên tháng đã nạp (02/2026 chưa nạp, không tính).${KN_SRC}`} actions={<span className={s.lb}>{kn.range}</span>}>
        <div className={s.knTop}>
          <Segmented label={t('crm.cda.k55')} value={period} onChange={(k) => setPeriod(k as KnPeriod)} items={KN_PERIODS.map((k) => ({ key: k, label: k }))} />
        </div>
        <div className={s.knKpi}>
          {kn.kpi.map((k) => (
            <div key={k.k} className={s.knKpiI}>
              <div className={s.lb}>{k.k}</div>
              <div className={s.big}><b>{k.v}</b><span className={s.lb}>{k.u}</span><ChgTag c={k.chg} tip={`Cùng kỳ: ${k.prev}`} /></div>
            </div>
          ))}
        </div>
        <Legend items={[['Kỳ chọn', 'var(--d-blue)'], ['Cùng kỳ năm trước', 'var(--d-amber)']]} note="nghìn USD" />
        <KnLine kn={kn} />
      </Card>
      <Card title={t('crm.cda.k62')} icon={IconChartPie} hint={`Số lô HANA làm (job HANA khớp tờ khai theo CustomsNo / ngày xuất) trên tổng số lô của khách, theo nước đến (ImportCountry). Nước khách đi nhiều mà HANA chưa làm được tô cam.${KN_SRC}`} actions={<span className={s.lb}>{kn.shareK}</span>}>
        <Legend items={[['HANA làm', 'var(--d-blue)'], ['Forwarder khác', 'var(--line)']]} note="lô HANA / tổng lô" />
        <div role="img" aria-label={kn.shareAria}>
          {kn.share.map((r) => (
            <div key={r.k} className={`${s.knRow} ${s.knShare} ${r.flag === 'Chưa làm' ? s.knGap : ''}`}>
              <span className={s.knName}><b>{r.k}</b>{r.flag && <span className={`${s.knPill} ${r.flag === 'Chưa làm' ? s.knWarn : s.knSub}`}>{r.flag}</span>}</span>
              <div className={s.track}><span className={s.fill} style={{ width: `${r.w}%`, background: r.flag === 'Chưa làm' ? 'var(--d-amber)' : undefined }} /></div>
              <span className={`${s.r} ${s.tx2}`}>{r.n}</span>
              <b className={s.r}>{r.p}</b>
            </div>
          ))}
        </div>
      </Card>
      <Card title={t('crm.cda.k63')} icon={IconScale} hint={`Theo điều kiện giao hàng (ExportTerm) trên tờ khai. CIF / CFR / CPT / DAP: người bán (khách) chọn forwarder, Sale chào trực tiếp. FOB / FCA / EXW: bên mua chỉ định, cần đi qua đại lý ở nước đến.${KN_SRC}`}>
        <div className={s.knStack} role="img" aria-label={kn.termsAria}>
          {kn.terms.map((g) => <span key={g.k} style={{ width: `${g.w}%`, background: g.amber ? 'var(--d-amber)' : 'var(--d-blue)' }} />)}
        </div>
        {kn.terms.map((g, i) => (
          <div key={g.k} className={`${s.knTerm} ${i ? s.knBt : ''}`}>
            <span className={s.knDot} style={{ background: g.amber ? 'var(--d-amber)' : 'var(--d-blue)' }} aria-hidden="true" />
            <div className={s.cell2}>
              <span><b>{g.k}</b> <span className={s.lb}>{g.act}</span></span>
              <span className={s.lb}>{g.items.map((x) => `${x.k} ${x.n}`).join(' · ')}</span>
            </div>
            <div className={s.r}><b>{g.p}</b><div className={s.lb}>{g.n} lô</div></div>
          </div>
        ))}
      </Card>
      <Card title={t('crm.cda.k64')} icon={IconWorld} hint={`Xếp theo kim ngạch (ImportCountry). Cảng đến theo booking HANA và cảng hay đi của nước đó. Nước HANA làm dưới 35% số lô có gợi ý tuyến cần báo giá.${KN_SRC}`} actions={<span className={s.lb}>{t('crm.cdt.nghinUsdLoSo')}</span>}>
        <div role="img" aria-label={kn.ctryAria}>
          {kn.ctry.map((r, i) => (
            <div key={r.k} className={`${s.knRow} ${s.knCtry} ${i ? s.knBt : ''}`}>
              <div className={s.cell2}><b>{r.k}</b><span className={s.lb}>{r.port}</span></div>
              <div className={s.track}><span className={s.fill} style={{ width: `${r.w}%` }} /></div>
              <b className={s.r}>{r.usd}</b>
              <span className={`${s.r} ${s.tx2}`}>{r.lot}</span>
              <ChgTag c={r.chg} />
              <div className={s.r}>
                {r.sug ? (
                  <button type="button" className={s.link} title={`Gợi ý báo giá tuyến ${r.lane}`} onClick={soon('Báo giá chưa mở ở bản này')}>Báo giá {r.lane}</button>
                ) : (
                  <span className={s.lb}>{r.lane}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
      <Card title={t('crm.cda.k65')} icon={IconUsers} hint={`Theo Consignee trên tờ khai. Mới: lô đầu tiên trong 12 tháng gần nhất. Ngừng: có lô ở kỳ trước nhưng không có lô nào 6 tháng gần đây. Chấm xanh: HANA đã làm hàng cho người mua này.${KN_SRC}`} actions={<span className={s.lb}>{kn.buyK}</span>}>
        {kn.buyers.map((r, i) => (
          <div key={r.k} className={`${s.knRow} ${s.knBuy} ${i ? s.knBt : ''}`}>
            <span className={s.knName}>
              <span className={s.knDot} style={{ background: r.hana ? 'var(--d-blue)' : 'var(--line)' }} title={r.hanaK} aria-hidden="true" />
              <b>{r.k}</b><span className={s.tag}>{r.cc}</span>
            </span>
            <div className={s.track}><span className={s.fill} style={{ width: `${r.w}%` }} /></div>
            <b className={s.r}>{r.n}</b>
            <div>{r.st && <span className={`${s.knPill} ${r.st === 'Mới' ? s.knOk : s.knErr}`} title={r.stTip}>{r.st}</span>}</div>
          </div>
        ))}
      </Card>
      <Card title={t('crm.cda.k66')} icon={IconPackage} hint={`Theo HSCode / Commodity, tỷ trọng theo kim ngạch. Cờ dịch vụ: Kiểm dịch (chứng thư thuỷ sản), CO (theo nước đến: KR AK/VK, JP AJ/VJ, EU EUR.1 / REX, AU AANZ), Hun trùng (pallet gỗ ISPM 15).${KN_SRC}`}>
        <div role="img" aria-label={kn.hsAria}>
          {kn.hs.map((h, i) => (
            <div key={h.hs} className={`${s.knRow} ${s.knHs} ${i ? s.knBt : ''}`}>
              <b>{h.hs}</b>
              <span className={s.tx2}>{h.k}</span>
              <div className={s.track}><span className={s.fill} style={{ width: `${h.w}%` }} /></div>
              <b className={s.r}>{h.p}</b>
              <div className={s.chips}>{h.svc.map((x) => <span key={x.k} className={s.tag} title={x.tip}>{x.k}</span>)}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card title={t('crm.cda.k67')} icon={IconRoute} hint={`TransportMode theo lô; Customs (chi cục mở tờ khai) và ExportType (loại hình). Chi cục khách mở nhiều mà HANA chưa khai thuê có gợi ý dịch vụ khai thuê.${KN_SRC}`}>
        <div className={s.knStack} role="img" aria-label={kn.tmodeAria}>
          {kn.tmode.map((g) => <span key={g.k} style={{ width: `${g.w}%`, background: g.amber ? 'var(--d-amber)' : 'var(--d-blue)' }} />)}
        </div>
        <div className={s.knLeg}>
          {kn.tmode.map((g) => (
            <span key={g.k} className={s.knLegI}><span className={s.knDot} style={{ background: g.amber ? 'var(--d-amber)' : 'var(--d-blue)' }} />{g.k} <b>{g.n} lô · {g.p}</b></span>
          ))}
          <span className={s.grow} />
          {kn.etype.map((x) => <span key={x.k} title={x.tip}><b>{x.k}</b> {x.n}</span>)}
        </div>
        <Legend items={[['HANA khai thuê', 'var(--d-blue)'], ['Khách / đơn vị khác khai', 'var(--line)']]} note="tờ khai" />
        {kn.cus.map((r) => (
          <div key={r.k} className={`${s.knRow} ${s.knCus}`}>
            <span className={s.knName} title={r.k}>{r.k}</span>
            <div className={s.track}><span className={s.fill} style={{ width: `${r.w}%` }} /></div>
            <span className={`${s.r} ${s.tx2}`}>{r.h} / {r.n}</span>
            <div>{r.sug && <span className={`${s.knPill} ${s.knWarn}`}>{t('crm.cdt.goiYKhaiThue')}</span>}</div>
          </div>
        ))}
      </Card>
      </div>
      <aside className={s.col}>
      <Card title={t('crm.cda.k68')} icon={IconSparkles} hint={t('crm.cda.k69')}>
        {kn.ask.map((a, i) => (
          <div key={a.n} className={`${s.knAsk} ${i ? s.knBt : ''}`}>
            <span className={s.av}>{a.n}</span>
            <div className={s.cell2}><b>{a.k}</b><span className={s.lb} style={{ whiteSpace: 'normal' }}>{a.d}</span></div>
            <div className={s.knAskGo}>
              <Button onClick={soon('Chưa mở ghi ở bản này')}><IconPlus size={16} stroke={2} aria-hidden="true" />{t('crm.cdt.taoCoHoiBan')}</Button>
            </div>
          </div>
        ))}
      </Card>
      <Card title={t('crm.cda.k70')} icon={IconTargetArrow} hint={t('crm.cda.k71')}>
        {kn.opp.map((o, i) => (
          <div key={o.k} className={`${s.knOpp} ${i ? s.knBt : ''}`}>
            <span className={s.knDot} style={{ background: o.amber ? 'var(--d-amber)' : 'var(--d-blue)' }} aria-hidden="true" />
            <span title={o.tip}>{o.k}</span>
            <b>{o.v}</b>
          </div>
        ))}
      </Card>
        <Card title={t('crm.cda.k72')} icon={IconLink}>
          {['Cơ hội bán hàng', 'Thị phần · Market Share', 'Dữ liệu hải quan'].map((l) => (
            <button key={l} type="button" className={s.linkRow} onClick={soon('Chưa mở ở bản này')}>
              {l} <span className={s.arrow}>↗</span>
            </button>
          ))}
        </Card>
      </aside>
    </div>
  );
}

function CustomerDetailDesk() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { customerId } = useParams({ strict: false }) as { customerId?: string };
  const { data, error, isLoading, isError, refetch } = useRecord('crmCustomer', customerId);
  const forced = readState();
  const [tab, setTab] = useState<TabKey>('ov');
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (!data) state = 'empty';
  const ready = state === 'ready' && !!data;

  const d = data ? detailFor(data) : null;
  const my = data ? myChip(data.my) : null;
  const back = (
    <Link to="/ezy/customers" className={s.back} aria-label={t('crm.cda.k73')} title={t('crm.cda.k73')}>
      <IconChevronLeft size={18} stroke={2} aria-hidden="true" />
    </Link>
  );

  return (
    <Page kind="table">
      <PageHeader
        title={customerId ?? 'Khách hàng'}
        findHint={false}
        lead={
          <>
            {back}
            {ready && (
              <span className={s.mark} aria-hidden="true">
                {initials(d!.id).padEnd(2, d!.id.slice(1, 2).toUpperCase())}
              </span>
            )}
          </>
        }
        meta={
          ready && d && my ? (
            <>
              <Chip tone="ok">{t('crm.cdt.khachDangHopTac')}</Chip>
              <Chip>Customer</Chip>
              <Chip tone={my.tone === 'ok' ? 'ok' : my.tone === 'warn' ? 'warn' : 'neutral'}>myHANA · {my.tone === 'ok' ? `${my.text} user` : my.text}</Chip>
              {d.holdDo && <Chip tone="err">{t('crm.cdt.giuDoVuotHan')}</Chip>}
            </>
          ) : undefined
        }
        sub={
          ready && data && d ? (
            <span className={s.subRow}>
              <span className={s.subItem} title={`MST ${data.mst}`}>
                <IconId size={14} stroke={1.8} aria-hidden="true" />
                {data.mst}
              </span>
              <span className={s.subItem} title={`Sale ${d.owners[0]?.name}`}>
                <Av text={d.owners[0]?.name ?? data.sale} />
                {d.id === 'GREENBUD' ? 'DAVID 70% · TUANNT 30%' : data.sale}
              </span>
              <span className={s.subItem} title={`BKG chủ khách ${d.bkgMain}`}>
                <Av text={d.bkgMain} />
                {d.bkgMain}
              </span>
              <span className={s.subItem} title={`BKG backup ${d.bkgBackup}`}>
                <Av text={d.bkgBackup} />
                {d.bkgBackup}
              </span>
            </span>
          ) : undefined
        }
        actions={
          <>
            <HeaderTool label={t('crm.cda.k74')} icon={IconBell} n={3} />
            <HeaderTool label={t('crm.cda.k75')} icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {t('crm.x.createJob')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        {ready && d && data && (
          <div className={s.kpi}>
            {[
              ['Job 90 ngày', String(d.jobs90), 'job', ''],
              ['Đang chạy', '2', 'job', ''],
              ['Sắp cut-off', '2', 'job · 48 giờ', s.warnTx],
              ['Hạn mức', d.holdDo ? 'Vượt' : 'Trong mức', d.holdDo ? 'giữ DO' : '', d.holdDo ? s.errTx : ''],
              ['Quá hạn', '••••', 'số tiền ẩn', ''],
              ['Lần liên hệ cuối', d.interactions[0]?.at.slice(0, 6) ?? '—', d.interactions[0] ? `Gọi · ${d.interactions[0].by}` : '', ''],
            ].map(([k, v, sub, cls]) => (
              <div key={k} className={s.kpiCell}>
                <div className={s.lb}>{k}</div>
                <div className={s.kpiV}>
                  <b className={cls}>{v}</b>
                  <span className={s.lb}>{sub}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {ready && d && (
          <Segmented
            label={t('crm.cda.k76')}
            value={tab}
            onChange={(k) => (k === 'ov' || k === 'ct' || k === 'job' || k === 'perf' || k === 'kn' || k === 'ix' || k === 'cfg' || k === 'ar' || k === 'my' || k === 'refund' || k === 'wiki' ? setTab(k) : toast({ msg: 'Tab này chưa mở ở bản này' }))}
            items={tabsOf(d, t)}
          />
        )}
        <StateView
          state={state === 'ready' && !data ? 'empty' : state}
          title={state === 'empty' || !data ? 'Không tìm thấy khách' : undefined}
          desc={state === 'empty' || !data ? 'Khách này không còn trong danh bạ.' : undefined}
          icon={state === 'empty' || !data ? <IconUsers size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('crm.cdt.thuLai')}</Button> : state === 'empty' ? <Button onClick={() => void navigate({ to: '/ezy/customers' })}>{t('crm.cdt.veDanhSach')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          {d && tab === 'ct' && <ContactsTab d={d} />}
          {d && tab === 'job' && <JobsTab d={d} />}
          {d && tab === 'ix' && <IxTab d={d} />}
          {d && tab === 'cfg' && <CfgTab d={d} />}
          {d && tab === 'ar' && <ArTab d={d} />}
          {d && tab === 'my' && <MyTab d={d} />}
          {d && tab === 'wiki' && <WikiTab d={d} />}
          {d && tab === 'refund' && <RefundTab partner={refundPartner(d.id)} empty={d.jobRows.length === 0} />}
          {d && tab === 'kn' && <KnTab empty={d.exports.every((e) => e.value == null)} />}
          {d && tab === 'perf' && <PerfTab empty={d.jobRows.length === 0} />}
          {d && tab === 'ov' && (
            <div className={s.grid}>
              <div className={s.col}>
                <Card
                  title={t('crm.cda.k77')}
                  icon={IconPackage}
                  hint={t('crm.cda.k78')}
                  flush
                  actions={
                    <button type="button" className={s.linkAll} onClick={() => toast({ msg: 'Danh sách job chưa mở ở bản này' })}>
                      <IconList size={14} stroke={1.8} aria-hidden="true" />
                      Tất cả {d.jobsTotal} job
                    </button>
                  }
                >
                  {d.jobs.length === 0 ? (
                    <div className={s.empty}>{t('crm.cdt.khachChuaCoJob')}</div>
                  ) : (
                    <>
                      <div className={`${s.jobRow} ${s.jobHd}`} aria-hidden="true">
                        <span>Job</span>
                        <span>{t('crm.cdt.tuyen')}</span>
                        <span>ETD</span>
                        <span>{t('crm.cdt.trangThai')}</span>
                        <span>PIC</span>
                      </div>
                      {d.jobs.map((j) => (
                        <div key={j.code} className={s.jobRow}>
                          <span className={s.jobCode}>
                            <button type="button" className={s.link} onClick={() => navigate({ to: '/ezy/jobs/$jobId', params: { jobId: j.code } })}>
                              {j.code}
                            </button>
                            <span className={`${s.tag} ${j.kind === 'TR' ? s.tagTr : ''}`}>{j.kind}</span>
                          </span>
                          <span className={s.cell2}>
                            <span>{j.route}</span>
                            <span className={s.lb}>{j.box}</span>
                          </span>
                          <span>{j.etd}</span>
                          <span>
                            <span className={s.st}>
                              <span className={s.stDot} style={{ background: j.status.startsWith('BL') ? 'var(--d-green)' : j.status.includes('Standby') ? 'var(--d-amber)' : 'var(--d-blue)' }} aria-hidden="true" />
                              {j.status}
                            </span>
                          </span>
                          <span className={s.pics}>
                            <Av text={j.sale} />
                            <Av text={j.pic} />
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </Card>

                <Card title={t('crm.cda.k58')} icon={IconRoute} hint={t('crm.cda.k79')}>
                  <div className={s.routes} role="img" aria-label={`Job theo tuyến: ${d.routes.map((r) => `${r.lane} ${r.jobs}`).join(', ')}`}>
                    <div className={`${s.rtRow} ${s.rtHd}`}>
                      <span>{t('crm.cdt.tuyenHangChinh')}</span>
                      <span>{t('crm.cdt.tyTrongJob')}</span>
                      <span className={s.r}>Job</span>
                      <span className={s.r}>TEU</span>
                    </div>
                    {d.routes.map((r) => (
                      <div key={r.lane} className={`${s.rtRow} ${s.rtBody}`}>
                        <span className={s.cell2}>
                          <span>{r.lane}</span>
                          <span className={s.lb}>{r.carriers}</span>
                        </span>
                        <span className={s.track}>
                          <span className={s.fill} style={{ width: `${Math.round((r.jobs / d.routes[0]!.jobs) * 100)}%` }} />
                        </span>
                        <b className={s.r}>{r.jobs}</b>
                        <span className={s.r}>{r.teu}</span>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card
                  title={t('crm.cda.k80')}
                  icon={IconChartBar}
                  hint={t('crm.cda.k81')}
                  actions={
                    <button type="button" className={s.pillLine} onClick={() => toast({ msg: 'Phân tích kim ngạch chưa mở ở bản này' })}>
                      <IconChartBar size={14} stroke={1.8} aria-hidden="true" />
                      {t('crm.x.analyze')}
                    </button>
                  }
                >
                  {d.exports.length === 0 ? (
                    <div className={s.empty}>{t('crm.cdt.chuaCoToKhai')}</div>
                  ) : (
                    <div className={s.exp}>
                      <div>
                        <div className={s.big}>
                          <b>{d.exportTotal}</b>
                          <span className={s.lb}>tr USD</span>
                        </div>
                        <div className={s.chips}>
                          <Chip tone="ok">▲ {d.exportDelta}</Chip>
                          <Chip tone="info">HANA {d.hanaShare} lô</Chip>
                        </div>
                        <div className={s.expNote}>
                          {d.declarations} tờ khai · {d.lots} lô · cao điểm Jul–Sep (cột đậm)
                        </div>
                      </div>
                      <ExportBars d={d} />
                    </div>
                  )}
                </Card>

                <Card
                  title={t('crm.cda.k82')}
                  icon={IconBuildingSkyscraper}
                  actions={
                    <>
                      <button type="button" className={s.pillLine} onClick={noWrite}>
                        {t('crm.x.lookupTax')}
                      </button>
                      <button type="button" className={s.pillLine} onClick={noWrite}>
                        {t('crm.x.edit')}
                      </button>
                    </>
                  }
                >
                  <Kv
                    rows={[
                      ['Tên pháp lý', d.legal.nameVi],
                      ['Tên EN', d.legal.nameEn],
                      ['MST', data!.mst],
                      ['Địa chỉ', d.legal.address],
                      ['Vùng · tỉnh', d.legal.region],
                      ['Đại diện', d.legal.rep],
                      ['Chủ sở hữu', d.legal.owner],
                      ['Hạng · phân khúc', d.legal.tier],
                      ['Mặt hàng', d.legal.goods],
                      ['Tình trạng', d.legal.status],
                    ]}
                  />
                  <label className={s.sync}>
                    <input type="checkbox" defaultChecked onChange={noWrite} />
                    AutoSync
                  </label>
                </Card>

                <Card
                  title={t('crm.cda.k83')}
                  icon={IconMessageCircle}
                  actions={
                    <button type="button" className={s.pill} onClick={() => toast({ msg: 'Tab này chưa mở ở bản này' })}>
                      {t('crm.x.viewAll')}
                    </button>
                  }
                >
                  {d.interactions.length === 0 ? (
                    <div className={s.empty}>{t('crm.cdt.chuaGhiTuongTac')}</div>
                  ) : (
                    d.interactions.map((x, i) => (
                      <div key={i} className={s.ix}>
                        <span className={s.ixIc} aria-hidden="true">
                          <IconMessageCircle size={15} stroke={2} />
                        </span>
                        <div className={s.ixTx}>
                          <div className={s.ixHd}>
                            <b>
                              {x.channel} · {x.who}
                            </b>
                            <span className={s.lb}>
                              {x.at} · {x.by}
                            </span>
                            {x.channel === 'Zalo' && (
                              <button type="button" className={s.linkSm} onClick={() => toast({ msg: 'Hei chưa mở ở bản này' })}>
                                Hei ↗
                              </button>
                            )}
                          </div>
                          <div className={x.failed ? s.errTx : s.tx2}>{x.text}</div>
                        </div>
                      </div>
                    ))
                  )}
                </Card>

                <Card title={t('crm.cda.k84')} icon={IconActivity} defaultOpen={false} />
              </div>

              <aside className={s.col} aria-label={t('crm.cda.k85')}>
                <Card
                  title={t('crm.cda.k86')}
                  icon={IconChecklist}
                  actions={d.todos.length > 0 ? <span className={s.count}>{d.todos.length}</span> : undefined}
                >
                  {d.todos.length === 0 ? (
                    <div className={s.empty}>{t('crm.cdt.khongCoViec')}</div>
                  ) : (
                    d.todos.map((t) => (
                      <div key={t.title} className={s.todo}>
                        <div className={s.todoTx}>
                          <b>{t.title}</b>
                          <div className={s.lb}>
                            {t.owner} · {t.due}
                          </div>
                        </div>
                        <button type="button" className={s.pillLine} onClick={() => toast({ msg: 'Chưa mở ở bản này' })}>
                          {t.action}
                        </button>
                      </div>
                    ))
                  )}
                </Card>

                <Card title={t('crm.cda.k87')} icon={IconFileText} hint={t('crm.cda.k88')}>
                  <Kv
                    stack
                    rows={[
                      ['Mã khách', d.code],
                      ['Nhóm', d.group],
                      ['Khách từ', d.since],
                      ['Nguồn', d.source],
                      ['Sale', d.id === 'GREENBUD' ? 'DAVID 70% · TUANNT 30%' : data!.sale],
                      ['BKG chủ khách', `${d.bkgMain} · backup ${d.bkgBackup}`],
                      ['CS phụ trách', d.cs],
                      ['Cảng hay dùng', d.portCommon],
                      ['Hãng hay dùng', d.carrierCommon],
                      ['Cập nhật', `${d.updated} · ${d.updatedBy}`],
                      [
                        'Nhãn',
                        <span key="tags" className={s.chips}>
                          {d.tags.map((t) => (
                            <Chip key={t}>{t}</Chip>
                          ))}
                        </span>,
                      ],
                    ]}
                  />
                </Card>

                <Card title={t('crm.cda.k89')} icon={IconUserCircle}>
                  {d.owners.map((o) => (
                    <div key={o.name} className={s.person}>
                      <Av text={o.name} />
                      <div>
                        <b>{o.name}</b>
                        <div className={s.lb}>{o.role}</div>
                      </div>
                    </div>
                  ))}
                  <div className={s.btns}>
                    <button type="button" className={s.pillLine} onClick={noWrite}>
                      Chia 2 Sale
                    </button>
                    <button type="button" className={s.pillLine} onClick={noWrite}>
                      {t('crm.x.handoverSale')}
                    </button>
                  </div>
                </Card>

                <Card
                  title={t('crm.cda.k90')}
                  label={t('crm.cda.k91')}
                  icon={IconCoin}
                  actions={
                    <button type="button" className={s.pill} onClick={() => setTab('ar')}>
                      {t('crm.x.detail')}
                    </button>
                  }
                >
                  <div className={s.lock}>
                    <IconLock size={14} aria-hidden="true" />
                    {t('crm.x.moneyHidden')}
                  </div>
                  {d.holdDo && (
                    <div className={`${s.over}`}>
                      <IconAlertTriangle size={14} stroke={1.8} aria-hidden="true" />
                      {t('crm.x.overLimit')}
                    </div>
                  )}
                  {AGING.map(([k]) => (
                    <div key={k} className={s.ageRow}>
                      <span className={s.lb}>{k}</span>
                      <b>••••</b>
                    </div>
                  ))}
                </Card>

                <MainContacts d={d} />

                <Card title={t('crm.cda.k72')} icon={IconLink}>
                  {['Hợp đồng dịch vụ', 'Báo giá', 'Cơ hội bán hàng', 'Công nợ · SOA', 'Zalo OA'].map((l) => (
                    <button key={l} type="button" className={s.linkRow} onClick={() => toast({ msg: 'Chưa mở ở bản này' })}>
                      {l} <span className={s.arrow}>↗</span>
                    </button>
                  ))}
                </Card>
              </aside>
            </div>
          )}
        </StateView>
      </div>
    </Page>
  );
}

/** Máy bàn: Mint-CustomerDetail. iPad: Mint-Customers-iPad-Detail (2 cột 1fr + 340). */
export function CustomerDetail() {
  const layout = useLayout();
  return layout === 'pad' ? <CustomerPadDetail /> : <CustomerDetailDesk />;
}
